import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Wrench, EnvelopeSimple, Phone, MapPin, CheckCircle, WarningCircle, Star } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CAPABILITIES, capLabel } from "@/lib/repairerMeta";

const BLANK = {
  business_name: "", address: "", suburb: "", state: "", postcode: "",
  phone: "", email: "", website: "", capabilities: [], brands: "",
  primary_centre: false, manual_coords: false, lat: "", lng: "",
};

export default function AdminRepairers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/repairers")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(getErrorMessage(e, "Couldn't load repairers. Check your connection and retry.")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...BLANK }); setOpen(true); };
  const openEdit = (r) => {
    setEditing({
      ...BLANK, ...r,
      capabilities: r.capabilities || [],
      brands: r.brands || "",
      primary_centre: !!r.primary_centre,
      lat: r.lat ?? "", lng: r.lng ?? "", manual_coords: !!r.manual_coords,
    });
    setOpen(true);
  };

  const toggleCap = (key) => {
    setEditing((e) => {
      const has = e.capabilities.includes(key);
      return { ...e, capabilities: has ? e.capabilities.filter((k) => k !== key) : [...e.capabilities, key] };
    });
  };

  const save = async () => {
    if (!editing.business_name.trim()) { toast.error("Business name is required"); return; }
    setSaving(true);
    const payload = {
      ...editing,
      lat: editing.manual_coords && editing.lat !== "" ? Number(editing.lat) : null,
      lng: editing.manual_coords && editing.lng !== "" ? Number(editing.lng) : null,
    };
    try {
      if (editing.id) await adminApi.put(`/admin/repairers/${editing.id}`, payload);
      else await adminApi.post("/admin/repairers", payload);
      toast.success("Saved — location resolved on the map");
      setOpen(false); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.business_name}"?`)) return;
    await adminApi.delete(`/admin/repairers/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <AdminHeader
        title="Repairers"
        subtitle="OTM preferred repairers shown to customers on the interactive map. Mark Caravan HQs / key agents as a Primary Service Centre to highlight them with a star."
        actions={<PrimaryButton onClick={openNew} data-testid="repairer-new"><Plus size={16} /> Add Repairer</PrimaryButton>}
      />
      <div className="p-8">
        {loading ? (
          <div className="text-neutral-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3">
            <Wrench size={36} className="text-neutral-700" weight="duotone" /> No repairers yet.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="repairers-table">
              <thead>
                <tr className="text-left text-[0.65rem] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="px-5 py-3 font-semibold">Business</th>
                  <th className="px-5 py-3 font-semibold">Contact</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold">Capabilities</th>
                  <th className="px-5 py-3 font-semibold">Map</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {rows.map((r) => (
                  <tr key={r.id} data-testid="repairer-row" className="hover:bg-white/[0.02]">
                    <td className="px-5 py-4">
                      <div className="font-heading font-semibold text-white flex items-center gap-1.5">
                        {r.primary_centre && <Star size={14} weight="fill" className="text-[#73bf44]" data-testid="repairer-primary-star" />}
                        {r.business_name}
                      </div>
                      {r.website && <div className="text-xs text-neutral-500 mt-0.5">{r.website}</div>}
                    </td>
                    <td className="px-5 py-4 text-neutral-300">
                      {r.email && <div className="flex items-center gap-2"><EnvelopeSimple size={14} className="text-[#73bf44]" /> {r.email}</div>}
                      {r.phone && <div className="flex items-center gap-2 mt-1 text-neutral-400"><Phone size={14} className="text-[#73bf44]" /> {r.phone}</div>}
                    </td>
                    <td className="px-5 py-4 text-neutral-300">
                      <div className="flex items-start gap-2">
                        <MapPin size={14} className="text-neutral-500 mt-0.5" />
                        <span>{[r.address, r.suburb, r.state, r.postcode].filter(Boolean).join(", ") || "—"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {(r.capabilities || []).length === 0 ? (
                          <span className="text-neutral-600 text-xs">—</span>
                        ) : (
                          (r.capabilities || []).map((k) => (
                            <span key={k} className="text-[0.6rem] font-medium px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-300">{capLabel(k)}</span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      {r.lat != null && r.lng != null ? (
                        <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold text-[#73bf44]">
                          <CheckCircle size={14} weight="fill" /> Located
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold text-amber-400">
                          <WarningCircle size={14} weight="fill" /> Not located
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(r)} data-testid="repairer-edit" className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><PencilSimple size={16} /></button>
                        <button onClick={() => del(r)} data-testid="repairer-delete" className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">{editing?.id ? "Edit" : "Add"} Repairer</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Field label="Business Name"><input value={editing.business_name} onChange={(e) => setEditing({ ...editing, business_name: e.target.value })} className={inputCls} data-testid="repairer-business" placeholder="Outback Caravan Repairs" /></Field>
              <Field label="Street Address"><input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className={inputCls} data-testid="repairer-address" placeholder="12 Industrial Ave" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Suburb"><input value={editing.suburb} onChange={(e) => setEditing({ ...editing, suburb: e.target.value })} className={inputCls} data-testid="repairer-suburb" placeholder="Yatala" /></Field>
                <Field label="State"><input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} className={inputCls} data-testid="repairer-state" placeholder="QLD" /></Field>
                <Field label="Postcode"><input value={editing.postcode} onChange={(e) => setEditing({ ...editing, postcode: e.target.value })} className={inputCls} data-testid="repairer-postcode" placeholder="4207" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone"><input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className={inputCls} data-testid="repairer-phone" placeholder="(07) 3439 8477" /></Field>
                <Field label="Email"><input value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inputCls} data-testid="repairer-email" placeholder="service@example.com.au" /></Field>
              </div>
              <Field label="Website (optional)"><input value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className={inputCls} data-testid="repairer-website" placeholder="https://example.com.au" /></Field>

              <Field label="Repair capabilities" hint="Ticked capabilities let customers filter the map.">
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {CAPABILITIES.map((c) => {
                    const on = editing.capabilities.includes(c.key);
                    return (
                      <label key={c.key} className={`flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 text-sm transition-colors ${on ? "border-[#73bf44] bg-[#73bf44]/[0.08] text-white" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleCap(c.key)} className="accent-[#73bf44] w-4 h-4" data-testid={`repairer-cap-${c.key}`} />
                        {c.label}
                      </label>
                    );
                  })}
                </div>
              </Field>

              <Field label="Brands accredited (optional)" hint="Comma separated, e.g. Projecta, Truma, Dometic">
                <input value={editing.brands} onChange={(e) => setEditing({ ...editing, brands: e.target.value })} className={inputCls} data-testid="repairer-brands" placeholder="Projecta, Truma, Dometic" />
              </Field>

              <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-[#73bf44]/40 bg-[#73bf44]/[0.06] px-3 py-2.5">
                <input type="checkbox" checked={editing.primary_centre} onChange={(e) => setEditing({ ...editing, primary_centre: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="repairer-primary" />
                <Star size={16} weight="fill" className="text-[#73bf44]" />
                <span className="text-sm text-neutral-200">Primary Service Centre <span className="text-neutral-500">(Caravan HQ / key agent — shown with a star)</span></span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input type="checkbox" checked={editing.manual_coords} onChange={(e) => setEditing({ ...editing, manual_coords: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="repairer-manual-coords" />
                <span className="text-sm text-neutral-300">Set map location manually (otherwise it's found from the address)</span>
              </label>
              {editing.manual_coords && (
                <div className="grid grid-cols-2 gap-3 border-l-2 border-[#73bf44]/40 pl-4">
                  <Field label="Latitude"><input value={editing.lat} onChange={(e) => setEditing({ ...editing, lat: e.target.value })} className={inputCls} data-testid="repairer-lat" placeholder="-27.81" /></Field>
                  <Field label="Longitude"><input value={editing.lng} onChange={(e) => setEditing({ ...editing, lng: e.target.value })} className={inputCls} data-testid="repairer-lng" placeholder="153.21" /></Field>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={save} disabled={saving} data-testid="repairer-save">{saving ? "Saving…" : "Save"}</PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
